<template>
	<div class="stretch-layout">
		<fetch-loader v-if="pending || error" :pending="pending" :error="error" @retry="refresh" :color="userStore.user.color" fill/>
		<row v-else>
			<column v-if="$device.isDesktop || $device.isTablet" cols="12" md="7" lg="8">
				<card xs-tile>
					<activity-feed ref="feedElement"
								   :url="`/v1/users/${userStore.user.id}/activities`"
								   :model="userStore.user" :without-create="$auth.id !== userStore.user.id" flat/>
				</card>
			</column>
			<column cols="12" md="5" lg="4">
				<card>
					<card-title>{{ $t('label.about_name', {name: userStore.user.greeting_name}) }}</card-title>
					<card-text>
						<p v-if="detailsData.profile.description" class="whitespace-pre-wrap">{{ detailsData.profile.description }}</p>
						<template v-if="detailsData.profile.age || detailsData.profile.coarse_address">
							<description-list class="border-b mb-16 pb-16">
								<description-list-item v-if="detailsData.profile.age" :title="$t('label.age')" :text="detailsData.profile.age"/>
								<description-list-item v-if="detailsData.profile.coarse_address" :title="$t('label.lives_in')" :text="detailsData.profile.coarse_address"/>
							</description-list>
						</template>

						<lazy-achievements-list :model-key="userStore.user.id" model-name="user"/>
					</card-text>
				</card>

				<card v-if="detailsData.clubs.items.length">
					<card-title spaced-content>{{ $t('param_label.memberships', userStore.user.clubs_count, {count: userStore.user.clubs_count}) }}</card-title>
					<div v-for="club in detailsData.clubs.items" :key="'club-' + club.id" class="divided">
						<lazy-profile-preview flat :model="club" no-spacing class="lg:px-24"/>
					</div>
					<btn :to="$web('users.show.memberships', userStore.user._key)" text :color="userStore.user.color ?? 'primary'" class="rounded-t-none border-t w-full">
						<span>{{ $t('action.show_all') }}</span>
						<icon :icon="icons.next"/>
					</btn>
				</card>

				<card v-if="userStore.user.sportTypes.length">
					<card-title>{{ $t('label.sports') }}</card-title>

					<card-text>
						<chip v-for="sportType in userStore.user.sportTypes" :key="'sport-' + sportType.id" class="m-2"
							  :to="$app('sports-sport', {params: {sport: sportType._key}, state: {sport_ts: $date(sportType.updated_at).unix()}})">{{ sportType.title }}
						</chip>
					</card-text>
				</card>

				<card>
					<card-title>{{ $t('param_label.followings', detailsData.followings.count, {count: detailsData.followings.count}) }}</card-title>
					<card-text>
						<avatar-list v-if="detailsData.followings.items.length" :models="detailsData.followings.items" :size="36" :total="detailsData.followings.count" :max="10"
									 :show-resolver="(model) => $web(`${model.model_name}s.show`, model._key)"
									 :show-all-resolver="() => $web('users.followings.index', userStore.user._key)" no-wrap/>
						<lazy-missing-info v-else :icon="icons.follower" no-spacing :text="$t('missing.user.followings', {name: userStore.user.greeting_name})"/>
					</card-text>
					<btn v-if="detailsData.followings.items.length" :to="$web('users.followings.index', userStore.user._key)" text :color="userStore.user.color ?? 'primary'" class="rounded-t-none border-t w-full">
						<span>{{ $t('action.show_all') }}</span>
						<icon :icon="icons.next"/>
					</btn>
				</card>

				<lazy-social-list v-if="userStore.user.socialMedia && userStore.user.socialMedia.has_any" :social-media="userStore.user.socialMedia"/>

			</column>
		</row>
	</div>
</template>

<script setup lang="ts">

import {mdiAccountMultipleOutline, mdiAccountStarOutline} from '@mdi/js';
import {nextIcon} from "@spoferan/nuxt-spoferan/icons";
import {useUserStore} from "../../../store/user";
import {useIndexStore} from "../../../store";

const {$date, $device, $auth} = useNuxtApp();
const route = useRoute();
const userStore = useUserStore();
const indexStore = useIndexStore();

// This EP requires the authenticated user for data visibility when not a public profile, but we still want to support SSR for guests
const requiresAuth = (!userStore.user.is_public || !userStore.user.has_public_contact || !userStore.user.has_public_clubs) && $auth.loggedIn;

// This EP requires the authenticated user for data visibility
const {reactiveData: detailsData, pending, error, refresh} = await useApiFetch(`/v1/views/users/${userStore.user.id}/details`, {
	auth: requiresAuth,
	lazyError: true, // We render a custom error via FetchLoader on errors to allow users to still access the page for membership requests on authorization errors
	params: {
		ts: $date(userStore.user.updated_at).unix()
	}
})

const icons = {
	follower: mdiAccountStarOutline,
	next: nextIcon,
	member: mdiAccountMultipleOutline
};

const isFollowing = computed(() => {
	return indexStore.follows.findIndex(follow => follow.type === userStore.user.model_type && follow.id === userStore.user.id) > -1;
});

const feedElement = ref(null);

watch(isFollowing, () => {
	if (feedElement.value) {
		feedElement.value.reload(1, true, false);
	}
});

</script>

